import { LOCAL_STORAGE_KEYS } from '@/shared/constants/storage-keys';
import {
	getLocalStorageItem,
	removeLocalStorageItem,
	setLocalStorageItem,
} from '@/shared/utils/storage';

import { IInputFieldIdToValueMap } from '../types/form-filling-types';

const TTL_MS = 5 * 24 * 60 * 60 * 1000;

type DraftEntry = {
	values: IInputFieldIdToValueMap;
	savedAt: number;
};

type DraftMap = Record<string, DraftEntry>;

function isPersistable(value: IInputFieldIdToValueMap[string]): boolean {
	if (value === null) return false;
	if (value instanceof Blob) return false;
	if (typeof value === 'object' && 'file_path' in value && 'file_name' in value) return false;
	return true;
}

function pickPersistable(values: IInputFieldIdToValueMap): IInputFieldIdToValueMap {
	const out: IInputFieldIdToValueMap = {};
	for (const fieldId in values) {
		if (isPersistable(values[fieldId])) out[fieldId] = values[fieldId];
	}
	return out;
}

function isDraftEntry(value: unknown): value is DraftEntry {
	if (!value || typeof value !== 'object') return false;
	const entry = value as DraftEntry;
	return typeof entry.savedAt === 'number' && !!entry.values && typeof entry.values === 'object';
}

function readMap(): DraftMap {
	const raw = getLocalStorageItem(LOCAL_STORAGE_KEYS.FORM_FILL_DRAFTS, true);
	if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};

	const map: DraftMap = {};
	for (const [formId, entry] of Object.entries(raw)) {
		if (isDraftEntry(entry)) map[formId] = entry;
	}
	return map;
}

function writeMap(map: DraftMap): void {
	setLocalStorageItem(LOCAL_STORAGE_KEYS.FORM_FILL_DRAFTS, JSON.stringify(map));
}

function sweepExpired(map: DraftMap): { map: DraftMap; changed: boolean } {
	const now = Date.now();
	const next: DraftMap = {};
	let changed = false;
	for (const formId in map) {
		const entry = map[formId];
		if (now - entry.savedAt <= TTL_MS) next[formId] = entry;
		else changed = true;
	}
	return { map: next, changed };
}

export function loadFormFillDraft(formId: string): IInputFieldIdToValueMap | null {
	if (!formId) return null;

	const { map, changed } = sweepExpired(readMap());
	if (changed) writeMap(map);
	return map[formId]?.values ?? null;
}

export function saveFormFillDraft(formId: string, values: IInputFieldIdToValueMap): void {
	if (!formId) return;

	const persistable = pickPersistable(values);
	if (Object.keys(persistable).length === 0) return;

	const { map } = sweepExpired(readMap());
	map[formId] = { values: persistable, savedAt: Date.now() };
	writeMap(map);
}

export function clearFormFillDraft(formId: string): void {
	if (!formId) return;

	const map = readMap();
	if (!(formId in map)) return;
	delete map[formId];
	if (Object.keys(map).length === 0) removeLocalStorageItem(LOCAL_STORAGE_KEYS.FORM_FILL_DRAFTS);
	else writeMap(map);
}
